<template>
	<div
		:class="[
			$style['app-modal'],
			{
				[$style['app-modal--opened']]: isOpen,
				[$style['app-modal--closed']]: isClosed,
				[$style['app-modal--fixed']]: fixed
			}
		]"
	>
		<div
			ref="content"
			:class="[
				$style['app-modal__content'],
				{
					[$style['app-modal__content--animated']]: isAnimated
				}
			]"
		>
			<Surface
				ref="surface"
				v-testid="'app-modal'"
				v-click-outside="shake"
				:style="{ maxWidth: width, minWidth: minWidth }"
				:class="[
					$style['app-modal__surface'],
					{ [$style['app-modal__surface--ai-loading']]: aiLoading }
				]"
			>
				<BaseIconButton
					v-if="!prompt"
					:class="$style['app-modal__back']"
					@click.native="close"
				>
					<FontAwesomeIcon :icon="['far', 'times']" />
				</BaseIconButton>
				<slot />
			</Surface>
		</div>
	</div>
</template>
<script>
import Surface from '@c:essentials/Surface'
import { TransitionEnd, AnimationEnd } from '@/utils'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
export default {
	components: {
		Surface
	},
	props: {
		dialog: {
			type: Boolean,
			default: true
		},
		prompt: {
			type: Boolean,
			default: false
		},
		fixed: {
			type: Boolean,
			default: false
		},
		width: {
			type: String,
			default: 'auto'
		},
		minWidth: {
			type: String,
			default: '600px'
		},
		preventClosing: {
			type: Boolean,
			default: false
		},
		aiLoading: {
			type: Boolean,
			default: false
		}
	},
	emits: ['modal-toggle'],
	data () {
		return {
			isOpen: false,
			isClosed: false,
			isAnimated: false,
			scrollLockTarget: null,
			openedScrollX: null,
			openedScrollY: null
		}
	},
	created () {
		window.addEventListener('keyup', this.keyupHandler)
	},
	mounted () {
		this.scrollContainer = document.getElementById('dashboard-main')
		this.openedScrollX = this.scrollContainer ? this.scrollContainer.scrollLeft : window.pageXOffset
		this.openedScrollY = this.scrollContainer ? this.scrollContainer.scrollTop : window.pageYOffset
		const windowScrollX = window.pageXOffset
		const windowScrollY = window.pageYOffset

		const surface = this.$refs.surface && this.$refs.surface.$el
		if (!surface) {
			return
		}

		this.scrollLockTarget = surface
		disableBodyScroll(surface, {
			allowTouchMove: el => {
				if (surface.contains(el)) {
					return true
				}
				return false
			}
		})
		// body-scroll-lock@4.0.0-beta.0 applies `position: fixed` to <body> on
		// iOS via its own rAF, without a width and with unitless top/left.
		// The body then shrink-wraps, relayouts the entire page and fires every
		// ResizeObserver at once — on large tables this stalls or crashes the
		// WebKit WebContent process. Pin the geometry so sizes don't change.
		requestAnimationFrame(() => {
			if (document.body.style.position !== 'fixed') return
			document.body.style.width = '100%'
			this.$_bodyWidthPinned = true
			const top = document.body.style.top
			if (!top || !top.endsWith('px')) {
				document.body.style.top = `${-(windowScrollY || 0)}px`
			}
			const left = document.body.style.left
			if (!left || !left.endsWith('px')) {
				document.body.style.left = `${-(windowScrollX || 0)}px`
			}
		})
		setTimeout(() => {
			this.isOpen = true
		}, 0)
	},
	beforeDestroy () {
		this.restoreBodyScroll()
		window.removeEventListener('keyup', this.keyupHandler)
	},
	methods: {
		restoreWindowScrollPosition () {
			if (this.openedScrollX === null || this.openedScrollY === null) {
				return
			}

			window.requestAnimationFrame(() => {
				if (this.scrollContainer) {
					this.scrollContainer.scrollTo(this.openedScrollX, this.openedScrollY)
				} else {
					window.scrollTo(this.openedScrollX, this.openedScrollY)
				}
			})
		},
		restoreBodyScroll () {
			const target = this.scrollLockTarget || (this.$refs.surface && this.$refs.surface.$el)
			if (target) {
				enableBodyScroll(target)
			}
			this.scrollLockTarget = null
			if (this.$_bodyWidthPinned && document.body.style.position !== 'fixed') {
				document.body.style.width = ''
				this.$_bodyWidthPinned = false
			}
			this.restoreWindowScrollPosition()
		},
		async close () {
			if (!this.preventClosing) {
				this.isClosed = true

				await Promise.race([
					TransitionEnd(this.$refs.content, { once: true }),
					new Promise(resolve => setTimeout(resolve, 200))
				])

				this.restoreBodyScroll()
				this.$emit('modal-toggle')
			} else {
				this.shake()
			}
			return Promise.resolve()
		},
		async shake (event) {
			if (event && event.target && event.target.closest('.v-popper__popper')) {
				return
			}
			this.isAnimated = true
			await AnimationEnd(this.$refs['content'], { once: true })
			this.isAnimated = false
		},
		keyupHandler (event) {
			if (!this.prompt && event && event.keyCode === 27) {
				this.close()
			}
		}
	}
}
</script>
<style lang="scss" module>

    .app-modal {
        z-index: 9999;
        position: fixed;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        width: 100%;
        height: 100%;
        inset: 0;
        // padding: 100px 0;
        overflow: auto;
        overflow-x: hidden;
        overscroll-behavior: contain;

        &--fixed {
            overflow: hidden;
        }

        &::before {
           	content: "";
			position: fixed;
			inset: 0;
			width: 100%;
			height: 100%;
			background-color: rgb(0, 0, 0, 0.32);
			opacity: 0;
			transition: opacity 0.15s linear;
			z-index: -1;
        }

        &__content {
            display: flex;
            justify-content: center;
            align-items: center;
            transition: opacity 0.045s linear, transform 0.15s cubic-bezier(0, 0, 0.2, 1);
            opacity: 0;
            transform: scale(0.8);
            width: 100vw;
            min-height: 100vh;
            will-change: transform;
            padding: $gutter * 3;

            @include media-breakpoint-only(xs) {
                padding: $gutter * 2;
            }

            &--animated {
                animation: kf-shake 0.4s 1 linear;
            }
        }

        &__surface {
            margin: 0 auto;
            width: auto;
            background-color: getColor(surface-elevated);
            position: relative;
            transition: width 2s ease;

            @include media-breakpoint-down(md) {
                min-width: auto !important;
                width: 100%;
            }

            // AI Loading Glow Effect - Inset Border Style
            &--ai-loading {
                // Important: contain the absolute pseudo-element within the rounded corners
                overflow: hidden;

                &::after {
                    content: "";
                    position: absolute;
                    // Cover the entire surface area
                    inset: 0;
                    // Sit on top of the content
                    z-index: 10;
                    // Ensure clicks pass through to form elements below
                    pointer-events: none;

                    // The glowing gradient pattern
                    background: linear-gradient(
                        45deg,
                        #00C6FB, #005BEA, #9d00ff, #ff0073,
                        #ff6b00, #ffd500, #00C6FB
                    );
                    background-size: 300%;

                    // SLOWER ANIMATION (12s)
                    animation: glowing 12s linear infinite;

                    // LOWER OPACITY (0.6)
                    opacity: 0.6;

                    // THINNER BORDER (4px)
                    $border-thickness: 4px;

                    padding: $border-thickness;

                    // Standard syntax  /* The outer area to keep */

                    // Webkit syntax for broader compatibility
                    mask:
                        linear-gradient(#fff 0 0) content-box,
                        linear-gradient(#fff 0 0) border-box;
                    mask-composite: xor;

                    // SOFTER BLUR (5px)
                    filter: blur(5px);
                }
            }
        }

        &__back {
            position: absolute !important;
            right: $gutter * 2;
            top: $gutter * 2;
            font-size: 20px !important;
            // Ensure close button is clickable above the glow layer
            z-index: 11;
        }

        &--opened {
            &::before {
                opacity: 1;
            }

            .app-modal__content {
                opacity: 1;
                transform: scale(1);
            }
        }

        &--closed {
            pointer-events: none;

            &::before {
                opacity: 0;
            }

            .app-modal__content {
                opacity: 0;
            }
        }
    }

    @keyframes product-fade-in {
        from {
            opacity: 0;
            transform: translateY(-100%);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes kf-shake {
        0% {
            transform: translate(25px);
        }

        20% {
            transform: translate(-25px);
        }

        40% {
            transform: translate(15px);
        }

        60% {
            transform: translate(-15px);
        }

        80% {
            transform: translate(8px);
        }

        100% {
            transform: translate(0);
        }
    }

    @keyframes glowing {
        0% {
            background-position: 0 0;
        }

        50% {
            background-position: 400% 0;
        }

        100% {
            background-position: 0 0;
        }
    }
</style>
